/**
 * Thread.jsx
 * The middle panel: what was said, and the box you answer it in.
 *
 * Two things here are load-bearing.
 *
 * **A reply and an internal note are never the same control.** They are
 * separate tabs, the note box is a different colour, and its button says
 * "Save note" rather than "Send". A note mistaken for a reply is the one bug
 * on this screen that would be published under the brand's name.
 *
 * **A suggested reply is never sent by pressing the suggestion.** Choosing one
 * fills the box, and you still press Send. The model writes; a person sends.
 *
 * Replying goes through the existing endpoints, which own the 24-hour message
 * window and the claim that stops two runs answering the same person twice.
 *
 * Dependencies: services/api.js, shared.js
 */

import { useEffect, useRef, useState } from 'react';
import { Check, Clipboard, Refresh, Send, Sparkle, Warning } from '../Icons.jsx';
import { api } from '../../services/api.js';
import { PLATFORMS } from '../dashboard/helpers.js';
import { SENTIMENT, clock, initials, when } from './shared.js';

export default function Thread({ detail, onChanged, onError, onResolve, busy }) {
  const [tab, setTab] = useState('reply');
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [thread, setThread] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [notice, setNotice] = useState(null);

  const { item, state, notes = [] } = detail;
  const key = `${item.kind}:${item.refId}`;
  const lastKey = useRef(null);

  // A different conversation is a different draft. Carrying one person's
  // half-written reply into somebody else's thread is how it gets sent to the
  // wrong person.
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    setText('');
    setNote('');
    setSuggestions([]);
    setNotice(null);
    setTab('reply');
    setThread(null);

    (async () => {
      try {
        setThread(
          item.kind === 'comment'
            ? await api.inbox.thread(item.refId)
            : await api.inbox.conversation(item.refId),
        );
      } catch {
        setThread(null);
      }
    })();
  }, [key, item.kind, item.refId]);

  const platform = PLATFORMS[item.platform];

  /** Sends. The one action on this panel that reaches a stranger. */
  async function send() {
    const body = text.trim();
    if (!body) return;

    setSending(true);
    setNotice(null);
    try {
      if (item.kind === 'comment') await api.inbox.reply(item.refId, body);
      else await api.inbox.replyToConversation(item.refId, body);

      setText('');
      setNotice('Sent.');
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setSending(false);
    }
  }

  async function saveNote() {
    const body = note.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.social.addNote(item.kind, item.refId, body);
      setNote('');
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setSending(false);
    }
  }

  /**
   * Deletes a comment from Facebook or Instagram, for everyone, permanently.
   *
   * Asked twice — here, and by the API, which refuses without `confirm` — and
   * the question quotes the comment and names the platform, because "Delete?"
   * on its own is how the wrong comment goes. A top-level comment takes its
   * replies with it, on Meta and here, so the question says so.
   */
  async function removeComment(c) {
    const where = platform?.label ?? 'the platform';
    const quoted = String(c.message ?? '').trim().slice(0, 120);
    const withReplies = !c.parent_remote_id;

    if (
      !confirm(
        `Delete this comment from ${where} permanently?\n\n` +
          `${c.author_name ? `${c.author_name}: ` : ''}“${quoted || 'no text'}”\n\n` +
          `It is removed from the post for everyone and cannot be undone.` +
          (withReplies ? ' Any replies under it are deleted too.' : ''),
      )
    ) {
      return;
    }

    setSending(true);
    setNotice(null);
    try {
      const res = await api.inbox.remove(c.id);
      setNotice(
        res.alreadyGone
          ? `That comment had already been deleted on ${where}. Removed here too.`
          : `Deleted from ${where}.`,
      );

      // Another comment in this thread: redraw the thread without it. The one
      // this conversation is about: the conversation itself is gone, and the
      // refresh below closes it.
      if (c.id !== item.refId && item.kind === 'comment') {
        setThread(await api.inbox.thread(item.refId).catch(() => null));
      }
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setSending(false);
    }
  }

  /** Costs money, so it is a button rather than something that happens on open. */
  async function suggestReplies() {
    setSuggesting(true);
    setNotice(null);
    try {
      const res = await api.social.suggest(item.kind, item.refId);
      setSuggestions(res.replies);
      setNotice(`Three drafts, about £${(res.costPence / 100).toFixed(2)}. Nothing has been sent.`);
    } catch (e) {
      onError(e);
    } finally {
      setSuggesting(false);
    }
  }

  // A message conversation arrives as `messages`; a comment thread arrives as
  // `thread` — every comment on that post, including your own replies. Only
  // the first two were read before, so a comment always showed on its own.
  const messages = thread?.messages ?? thread?.comments ?? thread?.thread ?? [];
  const isComment = item.kind === 'comment';
  const canReply = thread?.window ? thread.window.open !== false : true;

  return (
    <section className="card thread">
      <header className="thread__head">
        <span className="conv__avatar conv__avatar--lg">
          <span className="conv__initials">{initials(item.who)}</span>
        </span>
        <div className="thread__who">
          <h2>{item.who || 'Someone'}</h2>
          <p>
            {platform && (
              <>
                <platform.Icon size={12} /> {platform.label}
              </>
            )}{' '}
            · {when(item.at)}
            {item.context && ` · on “${String(item.context).slice(0, 40)}”`}
          </p>
        </div>

        {state?.sentiment && (
          <span className={`badge badge--${SENTIMENT[state.sentiment]?.tone ?? 'muted'}`}>
            <Sparkle size={12} /> {SENTIMENT[state.sentiment]?.label}
          </span>
        )}

        {/* The action people reach for most often, where they are already
            looking. It also lives in the contact panel, but that panel can be
            scrolled past — and a conversation nobody can close is one that
            stays open. */}
        <button
          className={`btn btn--sm${state?.status === 'resolved' ? '' : ' btn--primary'}`}
          onClick={onResolve}
          disabled={busy}
        >
          <Check size={15} /> {state?.status === 'resolved' ? 'Reopen' : 'Mark resolved'}
        </button>

        {/* The comment this conversation is about. Its full record comes with
            the thread; until that arrives, what the list already knows. */}
        {isComment && (
          <button
            className="btn btn--sm btn--danger"
            onClick={() =>
              removeComment(
                thread?.comment ?? { id: item.refId, message: item.body, author_name: item.who },
              )
            }
            disabled={sending || busy}
          >
            Delete comment
          </button>
        )}
      </header>

      <div className="thread__body">
        {messages.length === 0 ? (
          <div className="bubble bubble--them">
            <div className="bubble__text">{item.body || <em>No text</em>}</div>
            <div className="bubble__meta">{when(item.at)}</div>
          </div>
        ) : (
          messages.map((m, i) => {
            const ours = m.from_us || m.is_ours;
            // The comment this conversation was opened on, marked so it can be
            // told apart from the other comments on the same post.
            const isThisOne = isComment && m.id === item.refId;
            return (
              <div
                className={`bubble bubble--${ours ? 'us' : 'them'}${isThisOne ? ' bubble--focus' : ''}`}
                key={m.id ?? i}
              >
                <div className="bubble__who">
                  {ours ? 'You' : m.author_name || item.who}
                  {m.parent_remote_id && <span className="bubble__tag"> · reply</span>}
                </div>
                <div className="bubble__text">{m.message}</div>
                <div className="bubble__meta">
                  {clock(m.sent_at ?? m.posted_at)}
                  {/* Comments only. A direct message cannot be deleted through
                      Meta's API, so the link is not offered there. */}
                  {isComment && m.id && (
                    <button
                      type="button"
                      className="bubble__delete"
                      onClick={() => removeComment(m)}
                      disabled={sending || busy}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}

        {/* Internal notes sit in the thread where the team wrote them, but can
            never be mistaken for something that was said to the customer. */}
        {notes.map((n) => (
          <div className="note" key={n.id}>
            <div className="note__head">
              <Clipboard size={13} />
              <strong>{n.author ? n.author.split('@')[0] : 'Someone'}</strong> added an internal
              note
              <span className="note__when">{when(n.created_at)}</span>
            </div>
            <div className="note__body">{n.body}</div>
            <div className="note__flag">Not visible to {item.who || 'them'}</div>
          </div>
        ))}
      </div>

      {notice && (
        <div className="notice notice--info">
          <Check />
          <span>{notice}</span>
        </div>
      )}

      {!canReply && thread?.window?.reason && (
        <div className="notice notice--error">
          <Warning />
          <span>{thread.window.reason}</span>
        </div>
      )}

      {/* Suggestions fill the box. They are never sent by being chosen. */}
      <div className="suggest">
        <div className="suggest__head">
          <Sparkle size={15} />
          <span>AI suggested replies</span>
          <button
            className="btn btn--sm"
            onClick={suggestReplies}
            disabled={suggesting || busy}
            title="Writes three drafts. Counts towards your monthly AI spend."
          >
            <Refresh size={14} /> {suggesting ? 'Writing…' : suggestions.length ? 'Again' : 'Suggest'}
          </button>
        </div>

        {suggestions.length > 0 && (
          <ul className="suggest__list">
            {suggestions.map((s, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => {
                    setTab('reply');
                    setText(s.text);
                  }}
                  title="Puts this in the reply box. It is not sent."
                >
                  <span className="suggest__label">{s.label || 'Option'}</span>
                  <span className="suggest__text">{s.text}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="composer">
        <div className="tab-row" role="tablist">
          <button
            role="tab"
            type="button"
            aria-selected={tab === 'reply'}
            className={`tab${tab === 'reply' ? ' is-active' : ''}`}
            onClick={() => setTab('reply')}
          >
            Reply
          </button>
          <button
            role="tab"
            type="button"
            aria-selected={tab === 'note'}
            className={`tab${tab === 'note' ? ' is-active' : ''}`}
            onClick={() => setTab('note')}
          >
            Internal note
          </button>
        </div>

        {tab === 'reply' ? (
          <>
            <textarea
              className="composer__box"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Write a reply to ${(item.who || 'them').split(' ')[0]}…`}
              maxLength={2000}
              rows={3}
              disabled={!canReply}
            />
            <div className="composer__foot">
              <span className="row__sub">
                {text.length}/2000 · goes to {platform?.label ?? 'the platform'} as{' '}
                {item.kind === 'comment' ? 'a public reply' : 'a direct message'}
              </span>
              <button
                className="btn btn--primary"
                onClick={send}
                disabled={sending || !text.trim() || !canReply}
              >
                <Send size={15} /> {sending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          </>
        ) : (
          <>
            <textarea
              className="composer__box composer__box--note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A note for your team. Nobody outside sees this."
              maxLength={2000}
              rows={3}
            />
            <div className="composer__foot">
              <span className="row__sub">Internal only. This is never sent anywhere.</span>
              <button className="btn" onClick={saveNote} disabled={sending || !note.trim()}>
                <Clipboard size={15} /> {sending ? 'Saving…' : 'Save note'}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
